// Copied from the AWSC-SDK-TEST reference implementation (src/connect/initializeConnect.ts). Two
// deliberate differences: the import paths point at this folder, and the listener fan-out uses
// Set.forEach instead of for...of, because this project compiles with target es5 and without
// downlevelIteration, where iterating a Set is a compile error.
import type {
  CapabilityEntry,
  ConnectionStatus,
  InspectorError,
  InspectorEvent,
} from './types';
import { loadAwsModules, getExport } from './awsModules';
import { subscribeIfAvailable } from './subscriptionManager';
import { nowIso } from './nowIso';

const CONTACT_EVENTS = [
  'onIncoming',
  'onConnecting',
  'onConnected',
  'onPending',
  'onMissed',
  'onStartingAcw',
  'onCleared',
  'onError',
  'onParticipantAdded',
  'onParticipantDisconnected',
  'onParticipantStateChanged',
] as const;

const AGENT_EVENTS = [
  'onEnabledChannelListChanged',
  'onRoutingProfileChanged',
  'onAvailabilityStateChanged',
  'onNextAvailabilityStateChanged',
  'onNetworkConnectionStatusChanged',
  'onStateChanged',
] as const;

export interface InitializeConnectArgs {
  onStatusChange: (status: ConnectionStatus) => void;
  onEvent: (
    event: Omit<
      InspectorEvent,
      'id' | 'sequence' | 'timestamp' | 'timestampMs'
    >,
  ) => void;
  onError: (error: Omit<InspectorError, 'id' | 'timestamp'>) => void;
  onCapability: (entry: CapabilityEntry) => void;
}

export interface ConnectRuntime {
  cleanup: () => void;
  getProvider: () => unknown;
  getContactClient: () => Record<string, unknown> | undefined;
  getAgentClient: () => Record<string, unknown> | undefined;
  getVoiceClient: () => Record<string, unknown> | undefined;
}

interface ConnectListener {
  onStatusChange: InitializeConnectArgs['onStatusChange'];
  onEvent: InitializeConnectArgs['onEvent'];
  onError: InitializeConnectArgs['onError'];
  onCapability: InitializeConnectArgs['onCapability'];
}

interface ConnectSingletonState {
  bootPromise?: Promise<void>;
  listeners: Set<ConnectListener>;
  provider?: unknown;
  contactClient?: Record<string, unknown>;
  agentClient?: Record<string, unknown>;
  voiceClient?: Record<string, unknown>;
  status: ConnectionStatus;
  capabilities: CapabilityEntry[];
  subscriptionsRegistered: boolean;
}

const connectState: ConnectSingletonState = {
  listeners: new Set(),
  status: 'STARTING',
  capabilities: [],
  subscriptionsRegistered: false,
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

function extractLifecycleContactId(payload: unknown): string | undefined {
  const context = asRecord(asRecord(payload)?.context);
  const contactScope = asRecord(context?.contactScope);
  const directContactId = contactScope?.contactId;

  if (typeof directContactId === 'string' && directContactId) {
    return directContactId;
  }

  return extractContactId(payload);
}

function createClient(
  moduleObject: Record<string, unknown> | undefined,
  exportNames: string[],
  provider: unknown,
): Record<string, unknown> | undefined {
  const ClientCtor = getExport<new (config: { provider: unknown }) => unknown>(
    moduleObject,
    exportNames,
  );

  if (typeof ClientCtor !== 'function') {
    return undefined;
  }

  const instance = new ClientCtor({ provider });
  return asRecord(instance);
}

function isMultipleProviderInitializationError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message.includes('attemptInitializeMultipleProviders')
  );
}

function emitStatus(status: ConnectionStatus): void {
  connectState.status = status;
  connectState.listeners.forEach((listener) => listener.onStatusChange(status));
}

function emitEvent(
  event: Omit<InspectorEvent, 'id' | 'sequence' | 'timestamp' | 'timestampMs'>,
): void {
  connectState.listeners.forEach((listener) => listener.onEvent(event));
}

function emitError(error: Omit<InspectorError, 'id' | 'timestamp'>): void {
  connectState.listeners.forEach((listener) => listener.onError(error));
}

function emitCapability(entry: CapabilityEntry): void {
  connectState.capabilities = [
    ...connectState.capabilities.filter(
      (item) => !(item.client === entry.client && item.name === entry.name),
    ),
    entry,
  ].sort((left, right) =>
    `${left.client}.${left.name}`.localeCompare(
      `${right.client}.${right.name}`,
    ),
  );

  connectState.listeners.forEach((listener) => listener.onCapability(entry));
}

function registerClientSubscriptions(): void {
  if (connectState.subscriptionsRegistered) {
    return;
  }

  if (!connectState.contactClient && !connectState.agentClient) {
    return;
  }

  connectState.subscriptionsRegistered = true;

  if (connectState.contactClient) {
    emitCapability({
      client: 'ContactClient',
      name: 'constructor',
      status: 'AVAILABLE',
    });
    for (const eventName of CONTACT_EVENTS) {
      const result = subscribeIfAvailable(
        connectState.contactClient,
        eventName,
        (payload) => {
          emitEvent({
            source: 'contact',
            eventName,
            payload,
            contactId: extractContactId(payload),
          });
        },
      );

      emitCapability({
        client: 'ContactClient',
        name: eventName,
        status: result.subscribed ? 'AVAILABLE' : 'NOT_AVAILABLE',
        note: result.note,
      });
    }
  } else {
    emitCapability({
      client: 'ContactClient',
      name: 'constructor',
      status: 'FAILED',
      note: 'ContactClient export not found',
    });
  }

  if (connectState.agentClient) {
    emitCapability({
      client: 'AgentClient',
      name: 'constructor',
      status: 'AVAILABLE',
    });
    for (const eventName of AGENT_EVENTS) {
      const result = subscribeIfAvailable(
        connectState.agentClient,
        eventName,
        (payload) => {
          emitEvent({
            source: 'agent',
            eventName,
            payload,
          });
        },
      );

      emitCapability({
        client: 'AgentClient',
        name: eventName,
        status:
          eventName === 'onStateChanged' && result.subscribed
            ? 'DEPRECATED'
            : result.subscribed
              ? 'AVAILABLE'
              : 'NOT_AVAILABLE',
        note: result.note,
      });
    }
  } else {
    emitCapability({
      client: 'AgentClient',
      name: 'constructor',
      status: 'FAILED',
      note: 'AgentClient export not found',
    });
  }
}

function attachListener(args: InitializeConnectArgs): ConnectListener {
  const listener: ConnectListener = {
    onStatusChange: args.onStatusChange,
    onEvent: args.onEvent,
    onError: args.onError,
    onCapability: args.onCapability,
  };

  connectState.listeners.add(listener);
  listener.onStatusChange(connectState.status);
  for (const capability of connectState.capabilities) {
    listener.onCapability(capability);
  }

  return listener;
}

async function bootConnect(): Promise<void> {
  let provider: unknown;
  let reusedExistingProvider = false;
  connectState.subscriptionsRegistered = false;

  emitStatus('STARTING');

  const modules = await loadAwsModules();
  for (const packageError of modules.packageErrors) {
    emitError({
      operation: 'loadAwsModules',
      message: packageError,
      raw: packageError,
    });
  }

  const AmazonConnectApp = getExport<{
    init: (config: Record<string, unknown>) => { provider?: unknown };
    default?: unknown;
  }>(modules.appModule, ['AmazonConnectApp']);

  if (!AmazonConnectApp || typeof AmazonConnectApp.init !== 'function') {
    emitStatus('ERROR');
    emitCapability({
      client: 'AmazonConnectApp',
      name: 'init',
      status: 'FAILED',
      note: 'AmazonConnectApp.init export not found',
    });
    return;
  }

  emitCapability({
    client: 'AmazonConnectApp',
    name: 'init',
    status: 'AVAILABLE',
  });

  try {
    const initResult = AmazonConnectApp.init({
      onCreate: (event: unknown) => {
        emitStatus('CONNECTED');
        registerClientSubscriptions();
        emitEvent({
          source: 'lifecycle',
          eventName: 'onCreate',
          payload: event,
          contactId: extractLifecycleContactId(event),
          appInstanceId:
            asRecord(event)?.context &&
            typeof asRecord(event)?.context === 'object'
              ? String(asRecord(asRecord(event)?.context)?.appInstanceId ?? '')
              : undefined,
        });
      },
      onDestroy: (event: unknown) => {
        emitStatus('DESTROYING');
        emitEvent({
          source: 'lifecycle',
          eventName: 'onDestroy',
          payload: event,
        });
        emitStatus('DESTROYED');
      },
    });

    provider = initResult?.provider;
    emitStatus('WAITING_FOR_WORKSPACE');
    emitEvent({
      source: 'lifecycle',
      eventName: 'initCalled',
      payload: {
        timestamp: nowIso(),
        providerAvailable: Boolean(provider),
      },
    });
  } catch (error) {
    if (isMultipleProviderInitializationError(error)) {
      provider = AmazonConnectApp.default;
      reusedExistingProvider = true;

      emitStatus('WAITING_FOR_WORKSPACE');
      emitEvent({
        source: 'lifecycle',
        eventName: 'reusedExistingProvider',
        payload: {
          timestamp: nowIso(),
          reason:
            'AmazonConnectApp was already initialized in this page context.',
        },
        synthetic: true,
      });
    } else {
      emitStatus('ERROR');
      emitError({
        operation: 'AmazonConnectApp.init',
        name: error instanceof Error ? error.name : undefined,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        raw: error,
      });
      return;
    }
  }

  connectState.provider = provider;
  connectState.contactClient = createClient(
    modules.contactModule,
    ['ContactClient'],
    provider,
  );
  connectState.agentClient = createClient(
    modules.contactModule,
    ['AgentClient'],
    provider,
  );
  connectState.voiceClient = createClient(
    modules.voiceModule,
    ['VoiceClient'],
    provider,
  );

  if (connectState.voiceClient) {
    emitCapability({
      client: 'VoiceClient',
      name: 'constructor',
      status: 'AVAILABLE',
    });
    emitCapability({
      client: 'VoiceClient',
      name: 'getInitialCustomerPhoneNumber',
      status:
        typeof connectState.voiceClient.getInitialCustomerPhoneNumber ===
        'function'
          ? 'AVAILABLE'
          : 'NOT_AVAILABLE',
      note:
        typeof connectState.voiceClient.getInitialCustomerPhoneNumber ===
        'function'
          ? undefined
          : 'UNAVAILABLE IN INSTALLED SDK',
    });
  } else {
    emitCapability({
      client: 'VoiceClient',
      name: 'constructor',
      status: 'FAILED',
      note: 'VoiceClient export not found',
    });
  }

  if (connectState.status === 'CONNECTED' || reusedExistingProvider) {
    registerClientSubscriptions();
  }
}

export async function initializeConnect(
  args: InitializeConnectArgs,
): Promise<ConnectRuntime> {
  const listener = attachListener(args);

  if (!connectState.bootPromise) {
    connectState.bootPromise = bootConnect();
  }

  await connectState.bootPromise;

  return {
    cleanup: () => {
      connectState.listeners.delete(listener);
    },
    getProvider: () => connectState.provider,
    getContactClient: () => connectState.contactClient,
    getAgentClient: () => connectState.agentClient,
    getVoiceClient: () => connectState.voiceClient,
  };
}

export function extractContactId(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const record = payload as Record<string, unknown>;
  const direct = record.contactId;
  if (typeof direct === 'string') {
    return direct;
  }

  const contact = record.contact;
  if (contact && typeof contact === 'object') {
    const nested = (contact as Record<string, unknown>).contactId;
    if (typeof nested === 'string') {
      return nested;
    }
  }

  return undefined;
}
